<template>
  <v-row>
    <v-col cols="12" md="6">
      <v-card flat class="qt-form-card d-flex flex-column" height="calc(100vh - 82px)">
        <div class="qt-form-header pl-base-bg">
          <div class="qt-form-header-left">
            <div class="qt-form-header-icon">
              <v-icon color="#fff" size="22">mdi-file-document-edit-outline</v-icon>
            </div>
            <div>
              <div class="qt-form-title">Edit Quotation</div>
              <div class="qt-form-subtitle">{{ form.quotationNumber ? `Quotation #${form.quotationNumber}` : "Update quotation details" }}</div>
            </div>
          </div>
          <v-chip :color="statusColor(status)" dark small class="text-uppercase font-weight-bold">
            {{ status }}
          </v-chip>
        </div>

        <v-alert v-if="isConverted" type="info" outlined dense border="left" class="mx-6 mt-4">
          This quotation has been converted to an invoice and can no longer be edited.
        </v-alert>

        <v-form ref="form" v-model="valid" class="pa-6 overflow-y-auto flex-grow-1" :disabled="isConverted">
          <v-row dense>
            <v-col :cols="isMobile ? 12 : 4">
              <v-text-field
                v-model="form.quotationNumber"
                label="Quotation Number"
                outlined
                dense
                :disabled="isConverted"
                :rules="[rules.required, rules.uniqueQuotation]"
                prepend-inner-icon="mdi-pound"
              />
            </v-col>

            <v-col :cols="isMobile ? 12 : 4">
              <v-menu
                v-model="quotationMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formatDate(form.quotationDate)"
                    label="Quotation Date"
                    readonly
                    outlined
                    dense
                    :disabled="isConverted"
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="form.quotationDate" @change="quotationMenu = false" />
              </v-menu>
            </v-col>

            <v-col :cols="isMobile ? 12 : 4">
              <v-menu
                v-model="validUntilMenu"
                :close-on-content-click="false"
                offset-y
                left
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formatDate(form.validUntil)"
                    label="Valid Until"
                    readonly
                    outlined
                    dense
                    :disabled="isConverted"
                    prepend-inner-icon="mdi-calendar-clock"
                    append-icon="mdi-close-circle"
                    @click:append="form.validUntil = null"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="form.validUntil" @change="validUntilMenu = false" />
              </v-menu>
            </v-col>

            <v-col :cols="isMobile ? 12 : 6">
              <v-autocomplete
                v-model="selectedCustomer"
                :items="customers"
                item-text="name"
                item-value="_id"
                label="Select Customer"
                outlined
                dense
                :disabled="isConverted"
                prepend-inner-icon="mdi-account"
                return-object
                @change="onCustomerSelect"
              />
            </v-col>

            <v-col :cols="isMobile ? 12 : 6">
              <v-text-field
                v-model="form.customer.address"
                label="Customer Address"
                outlined
                dense
                :disabled="isConverted"
                prepend-inner-icon="mdi-map-marker"
              />
            </v-col>

            <v-col :cols="isMobile ? 12 : 6">
              <v-text-field
                v-model="form.customer.gstin"
                label="Customer GSTIN"
                outlined
                dense
                :disabled="isConverted"
                prepend-inner-icon="mdi-card-account-details"
              />
            </v-col>

            <v-col :cols="isMobile ? 12 : 6">
              <v-text-field
                v-model="form.placeOfSupply"
                label="Place of Supply (State)"
                outlined
                dense
                :disabled="isConverted"
                prepend-inner-icon="mdi-map-marker-radius"
              />
            </v-col>

            <v-col cols="12">
              <div class="qt-section-label">Items</div>

              <div
                v-for="(item, index) in form.items"
                :key="index"
                class="qt-item-row"
              >
                <v-btn icon class="qt-item-remove" :disabled="isConverted" @click="removeItem(index)">
                  <v-icon color="error" size="24">mdi-delete-outline</v-icon>
                </v-btn>

                <v-row dense>
                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      v-model="item.product"
                      label="Product / Description"
                      dense
                      outlined
                      hide-details
                      :disabled="isConverted"
                    />
                  </v-col>

                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      v-model="item.hsn"
                      label="HSN/SAC"
                      dense
                      outlined
                      hide-details
                      :disabled="isConverted"
                    />
                  </v-col>

                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      v-model.number="item.quantity"
                      type="number"
                      label="Qty"
                      dense
                      outlined
                      hide-details
                      :disabled="isConverted"
                      @input="calcItem(index)"
                    />
                  </v-col>
                </v-row>

                <v-row dense class="mt-1">
                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      v-model.number="item.price"
                      type="number"
                      label="Rate"
                      dense
                      outlined
                      hide-details
                      :disabled="isConverted"
                      @input="calcItem(index)"
                    />
                  </v-col>

                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      v-model.number="item.taxPercent"
                      type="number"
                      label="Tax % (estimate)"
                      dense
                      outlined
                      hide-details
                      :disabled="isConverted"
                      @input="calcItem(index)"
                    />
                  </v-col>

                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      :value="formatAmount(item.amount)"
                      label="Amount"
                      dense
                      outlined
                      readonly
                      hide-details
                    />
                  </v-col>
                </v-row>
              </div>

              <v-btn small class="qt-add-item-btn" :disabled="isConverted" @click="addItem">
                <v-icon left size="16">mdi-plus</v-icon>
                Add Product
              </v-btn>
            </v-col>

            <v-col cols="12">
              <v-divider class="my-4"></v-divider>
              <div class="totals-grid">
                <div>Subtotal</div>
                <div>₹ {{ formatAmount(subtotal) }}</div>
                <div>Estimated Tax</div>
                <div>₹ {{ formatAmount(gst) }}</div>
              </div>
              <div class="total-row mt-2">
                <span>Total</span>
                <strong>₹ {{ formatAmount(totalAmount) }}</strong>
              </div>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="form.note"
                label="Note"
                outlined
                dense
                rows="1"
                :disabled="isConverted"
                prepend-inner-icon="mdi-note-text"
              />
            </v-col>
          </v-row>
        </v-form>

        <v-row class="qt-form-actions px-6 flex-grow-0 flex-shrink-0" justify="end" dense>
          <v-col v-if="!isViewOnly" cols="4" sm="auto">
            <v-btn
              block
              color="error"
              outlined
              :disabled="isConverted"
              @click="confirmDelete = true"
            >
              Delete
            </v-btn>
          </v-col>

          <v-col cols="4" sm="auto">
            <v-btn block color="secondary" @click="fetchQuotation">Reset</v-btn>
          </v-col>

          <v-col v-if="!isViewOnly" cols="4" sm="auto">
            <v-btn
              block
              class="qt-save-btn"
              :loading="loading"
              :disabled="isConverted"
              @click="submit"
            >
              Update
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-col>

    <!-- RIGHT: PREVIEW -->
    <v-col cols="12" md="6">
      <v-card class="qt-preview-card d-flex flex-column" flat height="calc(100vh - 82px)">
        <div class="qt-preview-header">
          <v-icon color="#0a5a90" class="mr-2">mdi-file-document-multiple-outline</v-icon>
          <h3 class="qt-preview-title">Document</h3>
        </div>

        <v-row v-if="!isViewOnly && !isConverted" class="px-4 flex-grow-0 flex-shrink-0" no-gutters>
          <v-col cols="12">
            <v-btn
              block
              class="qt-generate-btn"
              @click="autoGenerateQuotation"
              :disabled="totalAmount <= 0 || !form.customer.name || !form.quotationNumber"
            >
              Generate Quotation
            </v-btn>
            <div v-if="!pdfGenerated" class="mt-2 text-caption" style="color: #b71c1c">
              Generate the quotation PDF before updating.
            </div>
          </v-col>
        </v-row>

        <!-- EXISTING FILE -->
        <div v-if="existingDocument && docState === 'keep'" class="mt-2 px-4 flex-shrink-0">
          <v-chip small color="primary">{{ existingDocument.fileName }}</v-chip>

          <v-btn
            v-if="!isViewOnly && !isConverted"
            small
            color="error"
            outlined
            class="ml-3"
            @click="removeFile"
          >
            Remove
          </v-btn>
        </div>

        <!-- PREVIEW -->
        <div class="viewer mt-4 mx-4">
          <img v-if="preview && isImage" :src="preview" class="preview-img" />

          <iframe v-else-if="preview" :src="preview" class="preview-frame" />

          <iframe
            v-else-if="existingDocument && docState === 'keep'"
            :src="existingDocument.fileUrl"
            class="preview-frame"
          />

          <div v-else class="empty-state">
            <v-icon size="40" color="#cbd5e1">mdi-file-document-outline</v-icon>
            <div class="mt-2">No document selected</div>
          </div>
        </div>
      </v-card>
    </v-col>

    <!-- DELETE -->
    <global-dialog
      v-if="confirmDelete"
      v-model="confirmDelete"
      :buttons="deleteDialogButtons"
      title="Confirm Delete"
    >
      <p>Are you sure you want to delete this quotation?</p>
    </global-dialog>
  </v-row>
</template>

<script>
import GlobalDialog from "./common/global-dialog.vue";
import { resolvePlaceOfSupplyState } from "../utils/gst";
import { PDF_COLORS, makePdfPrimitives } from "../utils/pdf-helpers";

export default {
  name: "EditQuotation",
  components: { GlobalDialog },

  data() {
    return {
      valid: false,
      loading: false,
      quotationMenu: false,
      validUntilMenu: false,
      status: "DRAFT",
      selectedCustomer: null,
      confirmDelete: false,
      deleting: false,

      file: null,
      preview: null,
      pdfGenerated: false,
      existingDocument: null,
      docState: "keep",

      form: {
        quotationNumber: "",
        quotationDate: "",
        validUntil: null,
        placeOfSupply: "",
        customer: {
          customerId: null,
          name: "",
          address: "",
          gstin: "",
          state: "",
        },
        items: [],
        note: "",
      },

      rules: {
        required: (v) => !!v || "Required",
        uniqueQuotation: (v) => {
          if (!v || !this.quotations.length) return true;
          const id = this.$route.params.id;
          return (
            !this.quotations.some(
              (q) => q._id !== id && q.quotationNumber?.toLowerCase() === v.toLowerCase(),
            ) || "Quotation number already exists"
          );
        },
      },
    };
  },

  created() {
    this.$store.dispatch("customers/getCustomers");
    this.$store.dispatch("quotations/getQuotations");
    this.fetchQuotation();
  },

  computed: {
    isConverted() {
      return this.status === "CONVERTED";
    },

    quotations() {
      return this.$store.state.quotations.list || [];
    },

    customers() {
      return this.$store.state.customers.customers;
    },

    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    subtotal() {
      return this.form.items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);
    },

    gst() {
      return this.form.items.reduce((sum, it) => {
        const amount = Number(it.amount) || 0;
        const percent = Number(it.taxPercent) || 0;
        return sum + (amount * percent) / 100;
      }, 0);
    },

    totalAmount() {
      return this.subtotal + this.gst;
    },

    isImage() {
      return this.file && this.file.type.startsWith("image/");
    },

    deleteDialogButtons() {
      return [
        {
          text: "Cancel",
          action: () => (this.confirmDelete = false),
          outlined: true,
          color: "#0a5a90",
        },
        {
          text: "Delete",
          action: this.deleteQuotation,
          color: "error",
          loading: this.deleting,
        },
      ];
    },
  },

  methods: {
    async fetchQuotation() {
      try {
        const id = this.$route.params.id;
        const res = await this.$store.dispatch("quotations/getQuotationById", id);
        const q = res.quotation;

        this.status = q.status;

        this.selectedCustomer = q.customer?.customerId
          ? { _id: q.customer.customerId, name: q.customer.name, gstin: q.customer.gstin, address: q.customer.address, state: q.customer.state }
          : null;

        this.form = {
          quotationNumber: q.quotationNumber || "",
          quotationDate: q.quotationDate ? new Date(q.quotationDate).toISOString().slice(0, 10) : "",
          validUntil: q.validUntil ? new Date(q.validUntil).toISOString().slice(0, 10) : null,
          placeOfSupply: q.placeOfSupply || "",
          customer: {
            customerId: q.customer?.customerId || null,
            name: q.customer?.name || "",
            address: q.customer?.address || "",
            gstin: q.customer?.gstin || "",
            state: q.customer?.state || "",
          },
          items: (q.items || []).map((it) => ({ ...it })),
          note: q.note || "",
        };

        this.existingDocument = res.document || null;
        this.preview = res.document?.fileUrl || null;
        this.file = null;
        this.pdfGenerated = false;
        this.docState = "keep";
      } catch (err) {
        this.$toast?.error("Failed to load quotation");
      }
    },

    removeFile() {
      this.file = null;
      this.preview = null;
      this.docState = "remove";
      this.pdfGenerated = false;
    },

    async autoGenerateQuotation() {
      try {
        await this.$store.dispatch("business/getProfile");

        const profile = this.$store.state.business?.profile;
        const customer = this.form.customer;

        if (!profile || !customer) {
          this.$toast.error("Seller or Customer missing");
          return;
        }

        const seller = {
          ...profile,
          address: [profile.address, profile.city, profile.state, profile.pincode, profile.country]
            .filter(Boolean)
            .join(", "),
        };

        const { jsPDF } = await import("jspdf");
        const autoTable = (await import("jspdf-autotable")).default;
        const doc = new jsPDF({ unit: "mm", format: "a4" });
        const pageW = doc.internal.pageSize.width;
        const pageH = doc.internal.pageSize.height;
        const M = 0;
        const CW = pageW;
        const PAD = 5;
        const FOOTER_H = 10;

        const { NAVY, WHITE, BGROW, BRDR } = PDF_COLORS;
        const { sc, rc, fr, wt, rl, fmtDate } = makePdfPrimitives(doc);

        let y = M;

        const badgeW = 52;
        const badgeH = 12;
        const badgeX = M + CW - badgeW;
        const badgeY = y;
        const leftW = badgeX - M - PAD - 4;
        const addrWrap = wt(seller.address || "", leftW);
        const contactStr = [seller.phone || "", seller.email || ""].filter(Boolean).join("   ·   ");
        const contactWrap = wt(contactStr, leftW);
        const headerH = Math.max(32, 8 + 7 + 3 + addrWrap.length * 3.8 + 2 + contactWrap.length * 3.8 + 4);

        fr(M, y, CW, headerH, NAVY);

        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        sc(...WHITE);
        doc.text(seller.businessName || "Company Name", M + PAD + 2, y + 10);

        let hSubY = y + 16;
        hSubY = rl(addrWrap, M + PAD + 2, hSubY, 7.5, "normal", [169, 183, 208], 3.8);
        hSubY += 2;
        rl(contactWrap, M + PAD + 2, hSubY, 7.5, "normal", [169, 183, 208], 3.8);

        const QUOTE_ACCENT = [180, 130, 20];
        fr(badgeX, badgeY, badgeW, badgeH, QUOTE_ACCENT);
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        sc(...WHITE);
        doc.text("QUOTATION", badgeX + badgeW / 2, badgeY + badgeH / 2 + 1.8, { align: "center", charSpace: 0.8 });

        if (seller.gstin) {
          doc.setFontSize(7.5);
          doc.setFont("helvetica", "normal");
          sc(169, 183, 208);
          doc.text(`GSTIN  ${seller.gstin}`, M + CW - 2, badgeY + badgeH + 6, { align: "right" });
        }
        rc();
        y += headerH;

        const infoH = 18;
        fr(M, y, CW, infoH, BGROW);
        doc.setDrawColor(...BRDR);
        doc.setLineWidth(0.3);
        doc.line(M, y + infoH, M + CW, y + infoH);

        const infoFields = [
          { label: "QUOTATION NO", value: this.form.quotationNumber || "-" },
          { label: "QUOTATION DATE", value: fmtDate(this.form.quotationDate) },
          { label: "VALID UNTIL", value: fmtDate(this.form.validUntil) },
          { label: "PLACE OF SUPPLY", value: this.form.placeOfSupply || "-" },
        ];
        const fw = CW / 4;
        infoFields.forEach((f, i) => {
          const fx = M + i * fw + PAD;
          doc.setFontSize(6.5);
          doc.setFont("helvetica", "normal");
          sc(...PDF_COLORS.MUTED);
          doc.text(f.label, fx, y + 7);
          doc.setFontSize(9.5);
          doc.setFont("helvetica", "bold");
          sc(...PDF_COLORS.DARK);
          doc.text(String(f.value), fx, y + 13.5);
        });
        y += infoH + 6;

        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        sc(...PDF_COLORS.MUTED);
        doc.text("QUOTATION FOR", M + PAD, y);
        y += 5;
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        sc(...PDF_COLORS.DARK);
        doc.text(customer.name || "-", M + PAD, y);
        y += 5;
        const custAddrWrap = wt(customer.address || "", CW - PAD * 2);
        y = rl(custAddrWrap, M + PAD, y, 8.5, "normal", PDF_COLORS.BODY, 4);
        if (customer.gstin) {
          doc.setFontSize(8);
          doc.setFont("helvetica", "normal");
          sc(...PDF_COLORS.BODY);
          doc.text(`GSTIN: ${customer.gstin}`, M + PAD, y + 4);
          y += 4;
        }
        y += 8;

        const rows = this.form.items.map((it) => [
          it.product || "-",
          it.hsn || "-",
          String(it.quantity || 0),
          this.formatAmount(it.price),
          `${it.taxPercent || 0}%`,
          this.formatAmount(it.amount),
        ]);

        autoTable(doc, {
          startY: y,
          head: [["Item", "HSN/SAC", "Qty", "Rate", "Tax %", "Amount"]],
          body: rows,
          margin: { left: M + PAD, right: M + PAD },
          styles: { fontSize: 8, cellPadding: 2.5 },
          headStyles: { fillColor: NAVY, textColor: WHITE },
        });

        y = doc.lastAutoTable.finalY + 8;

        const totalsW = 70;
        const totalsX = M + CW - PAD - totalsW;
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        sc(...PDF_COLORS.BODY);
        doc.text("Subtotal", totalsX, y);
        doc.text(this.formatAmount(this.subtotal), totalsX + totalsW, y, { align: "right" });
        y += 6;
        doc.text("Estimated Tax", totalsX, y);
        doc.text(this.formatAmount(this.gst), totalsX + totalsW, y, { align: "right" });
        y += 6;
        doc.setDrawColor(...BRDR);
        doc.setLineWidth(0.3);
        doc.line(totalsX, y, totalsX + totalsW, y);
        y += 6;
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        sc(...NAVY);
        doc.text("Total", totalsX, y);
        doc.text(this.formatAmount(this.totalAmount), totalsX + totalsW, y, { align: "right" });
        rc();
        y += 12;

        if (this.form.note) {
          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          sc(...PDF_COLORS.MUTED);
          doc.text("NOTE", M + PAD, y);
          y += 5;
          const noteWrap = wt(this.form.note, CW - PAD * 2);
          rl(noteWrap, M + PAD, y, 8.5, "normal", PDF_COLORS.BODY, 4);
        }

        const nPages = doc.internal.getNumberOfPages();
        for (let p = 1; p <= nPages; p++) {
          doc.setPage(p);
          fr(M, pageH - FOOTER_H, CW, FOOTER_H, NAVY);
          doc.setFontSize(7);
          doc.setFont("helvetica", "normal");
          sc(...WHITE);
          doc.text("This is a computer generated quotation, not a tax invoice.", M + PAD, pageH - FOOTER_H + 6);
          doc.text(`Page ${p} of ${nPages}`, M + CW - PAD, pageH - FOOTER_H + 6, { align: "right" });
        }
        rc();

        const blob = doc.output("blob");
        this.preview = URL.createObjectURL(blob);
        this.file = new File([blob], `quotation-${this.form.quotationNumber}.pdf`, { type: "application/pdf" });
        this.docState = "replace";
        this.pdfGenerated = true;
        this.$toast.success("Quotation Generated Successfully");
      } catch (e) {
        console.error(e);
        this.$toast.error("Quotation generation failed");
      }
    },

    onCustomerSelect(customer) {
      if (!customer) return;

      const gstin = customer.gstin || "";

      Object.assign(this.form.customer, {
        customerId: customer._id || null,
        name: customer.name || "",
        gstin: customer.gstin || "",
        state: customer.state || "",
        address: customer.address || "",
      });

      this.form.placeOfSupply =
        resolvePlaceOfSupplyState({ customerGstin: gstin, customerState: customer.state }) || this.form.placeOfSupply;
    },

    addItem() {
      this.form.items.push({
        product: "",
        hsn: "",
        quantity: 1,
        price: 0,
        taxPercent: 18,
        amount: 0,
      });
    },

    removeItem(i) {
      if (this.form.items.length === 1) return;
      this.form.items.splice(i, 1);
    },

    calcItem(i) {
      const item = this.form.items[i];
      if (!item) return;

      const qty = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;
      item.amount = +(qty * price).toFixed(2);
    },

    formatAmount(value) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value || 0);
    },

    formatDate(value) {
      if (!value) return "";
      const d = new Date(value);
      return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
    },

    statusColor(status) {
      switch (status) {
        case "DRAFT":
          return "grey";
        case "SENT":
          return "blue";
        case "ACCEPTED":
          return "green";
        case "REJECTED":
          return "red";
        case "EXPIRED":
          return "orange";
        case "CONVERTED":
          return "purple";
        default:
          return "grey";
      }
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      try {
        const id = this.$route.params.id;

        let fileBase64 = null;
        if (this.file) {
          fileBase64 = await new Promise((res) => {
            const reader = new FileReader();
            reader.onload = () => res(reader.result);
            reader.readAsDataURL(this.file);
          });
        }

        const payload = {
          id,
          quotationNumber: this.form.quotationNumber,
          quotationDate: this.form.quotationDate,
          validUntil: this.form.validUntil,
          placeOfSupply: this.form.placeOfSupply,
          customer: this.form.customer,
          items: this.form.items,
          note: this.form.note,
          file: fileBase64,
          fileName: this.file?.name || null,
          fileType: this.file?.type || null,
          existingDocumentId: this.existingDocument?._id || null,
          removeFile: this.docState === "remove",
        };

        await this.$store.dispatch("quotations/updateQuotation", payload);

        this.$toast?.success("Quotation updated successfully");
        this.$router.push(`/quotations/${id}/view`);
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to update quotation",
        );
      } finally {
        this.loading = false;
      }
    },

    async deleteQuotation() {
      this.deleting = true;
      try {
        await this.$store.dispatch("quotations/deleteQuotation", this.$route.params.id);
        this.$toast?.success("Quotation deleted");
        this.$router.push("/quotations");
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to delete quotation",
        );
      } finally {
        this.deleting = false;
        this.confirmDelete = false;
      }
    },
  },
};
</script>

<style scoped>
.qt-form-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.qt-form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
}

.qt-form-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.qt-form-header-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.qt-form-title {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
}

.qt-form-subtitle {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.qt-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.qt-item-row {
  position: relative;
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 14px 44px 14px 14px;
  margin-bottom: 12px;
}

.qt-item-remove {
  position: absolute;
  top: 6px;
  right: 6px;
}

.qt-add-item-btn {
  background: #eef3fb !important;
  color: #0a5a90 !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.totals-grid {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 24px;
  font-size: 13.5px;
  color: #475569;
  justify-items: end;
}
.totals-grid > div:nth-child(odd) {
  justify-self: start;
}

.total-row {
  display: flex;
  justify-content: space-between;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  border-top: 1px solid #eef1f5;
  padding-top: 8px;
}

.qt-form-actions {
  padding-top: 12px;
  padding-bottom: 16px;
}

.qt-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.qt-preview-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.qt-preview-header {
  display: flex;
  align-items: center;
  padding: 18px 20px 8px;
  flex-shrink: 0;
}

.qt-preview-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.qt-generate-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.preview-img {
  max-width: 100%;
}

.preview-frame {
  width: 100%;
  height: 100%;
  flex: 1;
  border: none;
}

.empty-state {
  color: #94a3b8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.viewer {
  flex: 1 1 auto;
  min-height: 0;
  margin-bottom: 16px;
  border: 1px dashed #cbd5e1;
  border-radius: 0;
  padding: 10px;
  background: #fafbfc;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
</style>
